import { ref, watch } from "vue";
import { api } from "@/api/client";
import { useAuthStore } from "@/stores/auth";

export type Theme = "light" | "dark" | "system";

const activeTheme = ref<Theme>("system");
let initialized = false;
let mediaQuery: MediaQueryList | null = null;

function applyTheme(theme: Theme) {
  const root = document.documentElement;
  if (theme === "dark") {
    root.classList.add("dark");
  } else if (theme === "light") {
    root.classList.remove("dark");
  } else {
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    root.classList.toggle("dark", prefersDark);
  }
}

function onSystemChange(e: MediaQueryListEvent) {
  if (activeTheme.value === "system") {
    document.documentElement.classList.toggle("dark", e.matches);
  }
}

export function initTheme() {
  if (initialized) return;
  initialized = true;

  const authStore = useAuthStore();

  // Set initial value from user preference or system default
  const userTheme = authStore.user?.theme as Theme | null | undefined;
  activeTheme.value = userTheme || "system";
  applyTheme(activeTheme.value);

  // Listen for system changes
  mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
  mediaQuery.addEventListener("change", onSystemChange);

  // Watch for user changes (login, fetchUser, etc.)
  watch(
    () => authStore.user?.theme,
    (newTheme) => {
      activeTheme.value = (newTheme as Theme) || "system";
      applyTheme(activeTheme.value);
    }
  );
}

export function useTheme() {
  const authStore = useAuthStore();

  async function setTheme(theme: Theme) {
    activeTheme.value = theme;
    applyTheme(theme);

    // Persist to backend if user is authenticated
    if (authStore.user?.id) {
      try {
        await api.patch(`/users/${authStore.user.id}`, { theme });
        authStore.user = { ...authStore.user, theme };
      } catch {
        // Silently fail — theme is still applied locally
      }
    }
  }

  return {
    activeTheme,
    setTheme,
  };
}
